import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { useCompany } from "./CompanyContext";
import { supabase } from "@/integrations/supabase/client";

export interface Branch {
  id: string;
  companyId: string;
  name: string;
  code: string;
  city: string;
  state?: string;
  address?: string;
  gstin?: string;
  phone?: string;
  email?: string;
  managerName?: string;
  isHeadOffice: boolean;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface BranchFinancials {
  branchId: string;
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
  invoicesCount: number;
  vouchersCount: number;
  inventoryValue: number;
  receivables: number;
  payables: number;
}

export interface BranchAssignment {
  id: string;
  branchId: string;
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  assignedBy: string;
  assignedAt: string;
  isActive: boolean;
}

interface BranchContextType {
  branches: Branch[];
  financials: BranchFinancials[];
  assignments: BranchAssignment[];
  selectedBranch: Branch | null;
  selectBranch: (id: string | null) => void;
  addBranch: (data: Omit<Branch, "id" | "createdAt" | "updatedAt" | "createdBy">) => Promise<Branch>;
  updateBranch: (id: string, data: Partial<Branch>) => Promise<void>;
  deleteBranch: (id: string) => Promise<void>;
  getBranchFinancials: (branchId: string) => BranchFinancials[];
  getConsolidated: () => BranchFinancials[];
  assignUser: (branchId: string, user: { userId: string; userName: string; userEmail: string; userRole: string }) => Promise<BranchAssignment>;
  removeAssignment: (id: string) => Promise<void>;
  updateAssignment: (id: string, data: Partial<BranchAssignment>) => Promise<void>;
  getBranchAssignments: (branchId: string) => BranchAssignment[];
  getUserBranches: (userId: string) => Branch[];
  hasAccessToBranch: (userId: string, branchId: string) => boolean;
  getAccessibleBranches: () => Branch[];
  loading: boolean;
}

const BranchContext = createContext<BranchContextType | null>(null);

function mapDbBranch(row: any): Branch {
  return {
    id: row.id,
    companyId: row.company_id || row.companyId || "",
    name: row.name,
    code: row.code,
    city: row.city || "",
    state: row.state || undefined,
    address: row.address || undefined,
    gstin: row.gstin || undefined,
    phone: row.phone || undefined,
    email: row.email || undefined,
    managerName: row.manager_name || row.managerName || undefined,
    isHeadOffice: Boolean(row.is_head_office),
    isActive: row.is_active !== undefined ? Boolean(row.is_active) : true,
    createdBy: row.created_by || "",
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function BranchProvider({ children }: { children: ReactNode }) {
  const { user, role, isAuthenticated } = useAuth();
  const { activeCompanyId } = useCompany();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [financials] = useState<BranchFinancials[]>([]);
  const [assignments, setAssignments] = useState<BranchAssignment[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);

  // Load branches from MySQL scoped strictly to activeCompanyId
  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated || !user || !activeCompanyId) {
      setBranches([]);
      setAssignments([]);
      setSelectedBranch(null);
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const [branchRes, permRes] = await Promise.all([
          supabase.from("branches").select("*").eq("company_id", activeCompanyId).order("name"),
          supabase.from("branch_permissions").select("*"),
        ]);

        if (cancelled) return;

        if (branchRes.data && Array.isArray(branchRes.data)) {
          const loadedBranches = branchRes.data.map(mapDbBranch);
          setBranches(loadedBranches);
          if (loadedBranches.length > 0) {
            setSelectedBranch(loadedBranches[0]);
          }
        }

        if (permRes.data && Array.isArray(permRes.data)) {
          setAssignments(permRes.data.map((r: any) => ({
            id: r.id,
            branchId: r.branch_id,
            userId: r.user_id,
            userName: r.user_name || "",
            userEmail: r.user_email || "",
            userRole: r.user_role || "accountant",
            assignedBy: r.assigned_by || "",
            assignedAt: r.assigned_at,
            isActive: Boolean(r.is_active),
          })));
        }
      } catch (err) {
        console.error("[BranchContext] Load error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id, activeCompanyId]);

  const selectBranch = useCallback((id: string | null) => {
    setSelectedBranch(id ? branches.find((b) => b.id === id) || null : null);
  }, [branches]);

  const addBranch = useCallback(async (data: Omit<Branch, "id" | "createdAt" | "updatedAt" | "createdBy">) => {
    const now = new Date().toISOString();
    const tempId = `br-${Date.now().toString(36)}`;

    const dbPayload = {
      id: tempId,
      company_id: data.companyId || user?.company || null,
      name: data.name,
      code: data.code,
      city: data.city || null,
      state: data.state || null,
      address: data.address || null,
      gstin: data.gstin || null,
      phone: data.phone || null,
      email: data.email || null,
      manager_name: data.managerName || null,
      is_head_office: data.isHeadOffice,
      is_active: data.isActive,
      created_by: user?.id || null,
    };

    const branch: Branch = {
      ...data,
      id: tempId,
      createdBy: user?.id || "admin",
      createdAt: now,
      updatedAt: now,
    };

    setBranches((prev) => [...prev, branch]);

    try {
      const { data: inserted, error } = await supabase.from("branches").insert(dbPayload).select().single();
      if (inserted) {
        setBranches((prev) => prev.map((b) => (b.id === tempId ? mapDbBranch(inserted) : b)));
      }
      if (error) console.error("[BranchContext] Insert error:", error);
    } catch (e) {
      console.error("[BranchContext] Exception on insert:", e);
    }

    return branch;
  }, [user]);

  const updateBranch = useCallback(async (id: string, data: Partial<Branch>) => {
    setBranches((prev) => prev.map((b) => (b.id === id ? { ...b, ...data, updatedAt: new Date().toISOString() } : b)));

    const dbUpdate: Record<string, any> = { updated_at: new Date().toISOString() };
    if (data.name !== undefined) dbUpdate.name = data.name;
    if (data.code !== undefined) dbUpdate.code = data.code;
    if (data.city !== undefined) dbUpdate.city = data.city;
    if (data.state !== undefined) dbUpdate.state = data.state;
    if (data.address !== undefined) dbUpdate.address = data.address;
    if (data.gstin !== undefined) dbUpdate.gstin = data.gstin;
    if (data.phone !== undefined) dbUpdate.phone = data.phone;
    if (data.email !== undefined) dbUpdate.email = data.email;
    if (data.managerName !== undefined) dbUpdate.manager_name = data.managerName;
    if (data.isHeadOffice !== undefined) dbUpdate.is_head_office = data.isHeadOffice;
    if (data.isActive !== undefined) dbUpdate.is_active = data.isActive;

    await supabase.from("branches").update(dbUpdate).eq("id", id);
  }, []);

  const deleteBranch = useCallback(async (id: string) => {
    setBranches((prev) => prev.filter((b) => b.id !== id));
    setAssignments((prev) => prev.filter((a) => a.branchId !== id));
    await supabase.from("branches").delete().eq("id", id);
  }, []);

  const getBranchFinancials = useCallback((branchId: string) => {
    return financials.filter((f) => f.branchId === branchId);
  }, [financials]);

  const getConsolidated = useCallback((): BranchFinancials[] => {
    return [];
  }, []);

  const assignUser = useCallback(async (branchId: string, userData: { userId: string; userName: string; userEmail: string; userRole: string }) => {
    const tempId = `ba-${Date.now().toString(36)}`;
    const assignment: BranchAssignment = {
      id: tempId,
      branchId,
      ...userData,
      assignedBy: user?.id || "admin",
      assignedAt: new Date().toISOString(),
      isActive: true,
    };

    setAssignments((prev) => [...prev, assignment]);

    await supabase.from("branch_permissions").insert({
      id: tempId,
      branch_id: branchId,
      user_id: userData.userId,
      user_name: userData.userName,
      user_email: userData.userEmail,
      user_role: userData.userRole,
      assigned_by: user?.id || null,
      is_active: true,
    });

    return assignment;
  }, [user]);

  const removeAssignment = useCallback(async (id: string) => {
    setAssignments((prev) => prev.filter((a) => a.id !== id));
    await supabase.from("branch_permissions").delete().eq("id", id);
  }, []);

  const updateAssignment = useCallback(async (id: string, data: Partial<BranchAssignment>) => {
    setAssignments((prev) => prev.map((a) => (a.id === id ? { ...a, ...data } : a)));
    const dbUpdate: Record<string, any> = {};
    if (data.isActive !== undefined) dbUpdate.is_active = data.isActive;
    if (data.userRole !== undefined) dbUpdate.user_role = data.userRole;
    await supabase.from("branch_permissions").update(dbUpdate).eq("id", id);
  }, []);

  const getBranchAssignments = useCallback((branchId: string) => {
    return assignments.filter((a) => a.branchId === branchId);
  }, [assignments]);

  const getUserBranches = useCallback((userId: string) => {
    if (role === "super_admin" || role === "company_admin") return branches;
    const userBranchIds = assignments.filter((a) => a.userId === userId && a.isActive).map((a) => a.branchId);
    return branches.filter((b) => userBranchIds.includes(b.id));
  }, [branches, assignments, role]);

  const hasAccessToBranch = useCallback((userId: string, branchId: string) => {
    if (role === "super_admin" || role === "company_admin") return true;
    return assignments.some((a) => a.userId === userId && a.branchId === branchId && a.isActive);
  }, [assignments, role]);

  const getAccessibleBranches = useCallback(() => {
    if (!user) return [];
    return getUserBranches(user.id);
  }, [user, getUserBranches]);

  return (
    <BranchContext.Provider value={{
      branches, financials, assignments, selectedBranch, selectBranch,
      addBranch, updateBranch, deleteBranch, getBranchFinancials, getConsolidated,
      assignUser, removeAssignment, updateAssignment, getBranchAssignments,
      getUserBranches, hasAccessToBranch, getAccessibleBranches, loading,
    }}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error("useBranch must be used within BranchProvider");
  return ctx;
}
